import React from 'react';
import objectPath from 'object-path';
import { isEmbeddableVideo, isPlaceholderNode } from '../utils/journey';
import 'react-medium-image-zoom/dist/styles.css';

import EmbeddableVideoPlayer from '../dashboard/EmbeddableVideoPlayer';
import JourneyExternalIntegration from '../dashboard/JourneyExternalIntegration';
import JourneyPDF from '../dashboard/JourneyPDF';
import JourneyEditorBlankPreview from '../dashboard/JourneyEditorBlankPreview';
import { DocumentViewer } from 'src/document-editor/document-viewer';
import { DocumentViewerEmpty } from 'src/document-editor/document-viewer-empty';
import { ZoomableImage } from './ZoomableImage';
import { JourneyUpsellPlayerView } from 'src/player/journey-upsell-player-view';
import ExternalLinkCard from 'src/common/components/external-link';
import OrganizationLink from './OrganizationLink';
import { getStepByNodeId } from 'src/utils/journey';
import { IconButton } from './icon-button';
import { EditStrongIcon } from 'src/monet/icons';

function JourneyViewContent(props) {
  const { node, journey, email, canEdit, inEditor, brandingColor, galleryMode, ...restProps } = props;

  if (!node) {
    return null;
  }

  const renderEmbeddableVideo = (node) => {
    return <EmbeddableVideoPlayer currentNode={node} {...restProps} />;
  };

  const renderIntegration = (node) => {
    return <JourneyExternalIntegration embed={node.embed} {...restProps} email={email || ''} />;
  };

  const renderPDF = (node) => {
    return <JourneyPDF {...node} {...restProps} />;
  };

  const renderImage = (node) => {
    const url = objectPath.get(node, 'image_asset.url');
    return (
      <ZoomableImage key={url} url={url} containerClassName='flex flex-1 max-w-full max-h-full image-content-wrapper' />
    );
  };

  const renderExternalLink = (node) => {
    const link = {
      ...node.external_link,
      title: node.external_link.name || node.name,
    };
    return <ExternalLinkCard link={link} {...restProps} disabled={galleryMode} />;
  };

  const renderFile = (node) => {
    const {
      file_asset: {
        url,
        name,
        data: { link_title, link_description, hide_image },
      },
    } = node;
    const link = {
      title: link_title || name || node.name,
      description: link_description,
      isThumbnailHidden: hide_image,
      url,
    };
    return (
      <ExternalLinkCard
        link={link}
        isThumbnailHidden={hide_image}
        {...restProps}
        disabled={galleryMode}
        isURLHidden
        isAttachment
      />
    );
  };

  const renderDocument = (node) => {
    const { document } = node;

    if (document.blocks.length === 0) {
      return (
        <div className="flex flex-1 flex-grow bg-white rounded-lg justify-center items-center overflow-y-auto">
          <DocumentViewerEmpty document={document} />
        </div>
      )
    }

    return (
      <div className="flex flex-1 flex-grow bg-white md:border-2 md:border-bedrock-light-gray rounded-lg justify-center overflow-y-auto">
        <div className="max-w-[680px] w-full">
          <DocumentViewer document={document} {...restProps} />
        </div>
        {inEditor && canEdit && journey && document && (
          <div className='absolute top-4 right-4'>
            <OrganizationLink to={`/journeys/edit/${journey.uuid}/section/${getStepByNodeId(journey, node.id).id}/step/${node.id}/document/${document.id}`}>
              <IconButton>
                <EditStrongIcon />
              </IconButton>
            </OrganizationLink>
          </div>
        )}
      </div>
    );
  };

  const renderPlaceholder = (node) => {
    return (
      <JourneyEditorBlankPreview
        node={node}
        brandingColor={brandingColor}
        showGuidance={false}
        showAdminGuidance={canEdit}
        showViewerGuidance={!canEdit}
      />
    );
  };

  const renderJourneyUpsell = () => {
    return <JourneyUpsellPlayerView />;
  };

  const contentType = node.content_type;

  if (contentType === 'journey_upsell') {
    return renderJourneyUpsell();
  }

  if (isPlaceholderNode(node)) {
    return renderPlaceholder(node);
  } else if (contentType === 'video' && isEmbeddableVideo(node)) {
    return renderEmbeddableVideo(node);
  } else if (contentType === 'pdf') {
    return renderPDF(node);
  } else if (contentType === 'external_link') {
    return renderExternalLink(node);
  } else if (contentType === 'image') {
    return renderImage(node);
  } else if (contentType === 'file') {
    return renderFile(node);
  } else if (contentType === 'journey_document') {
    return renderDocument(node);
  }

  return renderIntegration(node);
}

JourneyViewContent.defaultProps = {
  node: null,
};

export default JourneyViewContent;
